import React from 'react';
import { observer } from 'src/utils/mobx-react';
import { makeObservable, computed, action } from 'mobx';
import {
    DropdownSelect,
    type OptionProps,
    type GroupBase,
    type MultiValue,
} from 'src/components/DropdownWrapper/DropdownWrapper';
import {
    SettingsDropdownWrapper,
    SettingsDropdownArrow,
} from 'src/components/CmsCasinoManagement/CategoryHeader/CategoryHeader.style';
import { SimpleCheckbox } from 'src/components/CmsCasinoManagement/CategoryHeader/SimpleCheckbox/SimpleCheckbox';
import { DropdownOption, DropdownPlaceholder, getDropdownStyles, OptionWrapper } from './SettingsDropdown.style';

export type CategorySettingsValueType = 'active' | 'visible_in_navigation' | 'visible_in_all_games' | null;

export type CategorySettingsOptionType = {
    value: CategorySettingsValueType;
    label: string | JSX.Element;
    disabled?: boolean;
};

interface SettingsDropdownPropsType {
    optionsList: () => Array<CategorySettingsOptionType>;
    selectedValues: () => Array<CategorySettingsValueType>;
    onChangeCallback: (selectedOptions: Array<CategorySettingsOptionType>) => void;
    isDisabled?: boolean;
}

class SettingsDropdownState {
    public constructor(
        public readonly optionsList: () => Array<CategorySettingsOptionType>,
        public readonly selectedValues: () => Array<CategorySettingsValueType>,
        public readonly onChangeCallback: (selectedOptions: Array<CategorySettingsOptionType>) => void
    ) {
        makeObservable(this);
    }

    @action public isOptionSelected = (option: CategorySettingsOptionType): boolean => {
        return this.selectedValues().includes(option.value);
    };

    @action public formatValueLabel = (): string => {
        const labels = this.activeOptions.map((opt) => opt.label);
        return labels.join(', ');
    };

    @action public onChangeTrigger = (selectedOptions: MultiValue<CategorySettingsOptionType>): void => {
        this.onChangeCallback(Array.from(selectedOptions));
    };

    @computed public get optionsListToDisplay(): CategorySettingsOptionType[] {
        return this.optionsList();
    }

    @computed public get activeOptions(): Array<CategorySettingsOptionType> {
        return this.optionsListToDisplay.filter((option) => {
            return this.selectedValues().includes(option.value);
        });
    }
}

export const SettingsDropdown = observer('SettingsDropdown', (props: SettingsDropdownPropsType) => {
    const { optionsList, selectedValues, onChangeCallback, isDisabled } = props;
    const [state] = React.useState(() => new SettingsDropdownState(optionsList, selectedValues, onChangeCallback));

    const CustomOption = (
        optionProps: OptionProps<CategorySettingsOptionType, true, GroupBase<CategorySettingsOptionType>>
    ): JSX.Element => {
        const { data, innerRef, innerProps, isSelected, isDisabled: isOptionDisabled } = optionProps;
        const isDisabled = data.disabled === true || isOptionDisabled;

        if (data.value === null) {
            return <div />;
        }

        return (
            <OptionWrapper
                ref={innerRef}
                {...innerProps}
                isSelected={isSelected}
                role='option'
                aria-selected={isSelected}
                aria-disabled={isDisabled}
                data-test={`category-setting-${data.value}`}
            >
                <DropdownOption>
                    <span>{data.label}</span>
                    <SimpleCheckbox
                        isChecked={state.isOptionSelected(data)}
                        isDisabled={isDisabled}
                        onChange={function (): void {}}
                    />
                </DropdownOption>
            </OptionWrapper>
        );
    };

    const [menuIsOpen, setMenuIsOpen] = React.useState(false);

    return (
        <SettingsDropdownWrapper>
            <SettingsDropdownArrow isOpen={menuIsOpen} />
            <DropdownSelect<CategorySettingsOptionType, true>
                placeholder='Inactive'
                isMulti={true}
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                isClearable={false}
                className='custom-dropdown-select'
                onChange={state.onChangeTrigger}
                options={state.optionsListToDisplay}
                isSearchable={false}
                value={state.activeOptions}
                isDisabled={isDisabled}
                isOptionDisabled={(option) => option.disabled === true}
                onMenuOpen={() => setMenuIsOpen(true)}
                onMenuClose={() => setMenuIsOpen(false)}
                components={{
                    Option: CustomOption,
                    Placeholder: () => {
                        const hasValues = state.activeOptions.length > 0;
                        return (
                            <DropdownPlaceholder data-test='settings-dropdown-value'>
                                {hasValues ? state.formatValueLabel() : 'Inactive'}
                            </DropdownPlaceholder>
                        );
                    },
                }}
                controlShouldRenderValue={false}
                getOptionValue={(option) => (option.value === null ? '' : option.value.toString())}
                getOptionLabel={(option) => (typeof option.label === 'string' ? option.label : '')}
                customStyles={getDropdownStyles<CategorySettingsOptionType, true>()}
                hasInternalLabel={false}
            />
        </SettingsDropdownWrapper>
    );
});
